import { shallowEqual } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

import useAssignmentResultType from '@helpers/hooks/use-assignment-result-type';
import useCourseVersion from '@helpers/hooks/use-course-version';

import { mapUnassignAssignmentHashKeys } from '@components/footer/helpers';
import { showToast } from '@components/toast/toast-action';

import { ENTITY_NAMES } from '@containers/entities/constants';
import { deleteEntityItems } from '@containers/entities/entities-reducer';
import {
  deleteUnAssignAssignments,
  postAssignAssignments,
  getAssignments
} from '@containers/gateways/gateways-actions';

const getTranslations = ({ i18n }) => ({
  UNASSIGN_SUCCESS_TEXT: i18n.t`Student Quests have been unassigned`,
  ASSIGN_SUCCESS_TEXT: i18n.t`Student activity assignments have been successfully updated`,
  REASSIGN_SUCCESS_TEXT: i18n.t`Activity reassignment successful`
});

const useManageAssignments = () => {
  const dispatch = useDispatch();
  const { i18n } = useLocalisation();
  const { assignmentResultType } = useAssignmentResultType();
  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const { selectedClassroomId, selectedCourseId, selectedCourseVersion } = useCourseVersion();

  const getAssignmentData = {
    assignmentResultType,
    classId: selectedClassroomId,
    courseId: selectedCourseId,
    courseVersion: selectedCourseVersion,
    productId: selectedProduct?.id
  };

  const { UNASSIGN_SUCCESS_TEXT, ASSIGN_SUCCESS_TEXT, REASSIGN_SUCCESS_TEXT } = getTranslations({ i18n });

  const unassignAssignments = async (assignmentsToUnAssign, successToastText = UNASSIGN_SUCCESS_TEXT) => {
    await dispatch(deleteUnAssignAssignments(assignmentsToUnAssign))
      .then(() => {
        const deleteAssignmentIds = mapUnassignAssignmentHashKeys(assignmentsToUnAssign);

        dispatch(deleteEntityItems({ entityName: ENTITY_NAMES.ASSIGNMENTS, itemIds: deleteAssignmentIds }));
        dispatch(getAssignments(getAssignmentData));
        dispatch(showToast(successToastText, 'success'));
      })
      .catch(() => {
        dispatch(showToast(i18n.t`Unable to unassign selected student assignments`, 'error'));
      });
  };

  const assignAssignments = async ({
    studentAssignments,
    isReassignment = false,
    startDate = null,
    dueDate = null
  }) => {
    let assignmentsToAssign = {
      studentAssignments,
      productId: selectedProduct?.id,
      courseId: selectedCourseId,
      courseVersion: selectedCourseVersion,
      classroomId: selectedClassroomId
    };

    if (startDate && dueDate) {
      assignmentsToAssign = {
        ...assignmentsToAssign,
        startDate,
        dueDate
      };
    }

    await dispatch(postAssignAssignments(assignmentsToAssign)).then(() => {
      if (isReassignment) {
        dispatch(showToast(REASSIGN_SUCCESS_TEXT, 'success'));
      } else {
        dispatch(showToast(ASSIGN_SUCCESS_TEXT, 'success'));
      }
      dispatch(getAssignments(getAssignmentData));
    });
  };

  return {
    unassignAssignments,
    assignAssignments
  };
};

export default useManageAssignments;
